import { Home, UtensilsCrossed, HandHeart, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/config";

const REASONS = [
  {
    icon: Home,
    num: "01",
    title: "A Mile of Shelter",
    text: "Each mile funds transitional housing — a safe door to close, a warm bed, a real address for families rebuilding their lives.",
  },
  {
    icon: UtensilsCrossed,
    num: "02",
    title: "A Mile of Nourishment",
    text: "Your miles become meals and groceries for neighbors facing hunger during National Hunger & Homelessness Awareness Week.",
  },
  {
    icon: HandHeart,
    num: "03",
    title: "A Mile of Stability",
    text: "Registrations grow the capital resources that keep the lights on, cover a deposit, and turn a crisis into a comeback.",
  },
  {
    icon: Users,
    num: "04",
    title: "A Mile of Belonging",
    text: "When a community shows up at the start line, no one walks through hardship alone. Your presence is the message: you matter.",
  },
];

export const EveryMile = () => (
  <section id="miles" data-testid="miles-section" className="bg-white py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-4 md:px-8">
      <div className="mb-16 grid gap-6 lg:grid-cols-2 lg:items-end">
        <Reveal>
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-hope-green">
              The Impact
            </p>
            <h2
              data-testid="miles-heading"
              className="font-heading text-4xl font-black leading-[1.05] tracking-tighter text-hope-ink sm:text-5xl lg:text-6xl"
            >
              Every Mile
              <br />
              Means <span className="text-hope-red">Something.</span>
            </h2>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="max-w-md text-base leading-relaxed text-zinc-600 md:text-lg lg:ml-auto">
            Whether you race the clock in the timed 5K or stroll the fun walk
            with family, every mile you cover carries real weight for the
            people we serve.
          </p>
        </Reveal>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="miles-reasons-grid">
        {REASONS.map((r, i) => (
          <Reveal key={r.num} delay={i * 0.1}>
            <div
              data-testid={`mile-reason-${r.num}`}
              className="group flex h-full flex-col rounded-3xl border border-black/5 bg-hope-cream p-8 transition-all duration-300 hover:-translate-y-2 hover:border-hope-green/40 hover:shadow-md"
            >
              <div className="mb-8 flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-hope-green text-white transition-colors duration-300 group-hover:bg-hope-red">
                  <r.icon className="h-6 w-6" />
                </span>
                <span className="font-heading text-5xl font-black text-black/10 transition-colors duration-300 group-hover:text-hope-green/25">
                  {r.num}
                </span>
              </div>
              <h3 className="font-heading text-xl font-bold text-hope-ink">{r.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-600">{r.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="relative mt-16 overflow-hidden rounded-3xl">
          <img
            src={IMAGES.volunteers}
            alt="Volunteers serving the community"
            data-testid="miles-volunteers-image"
            className="h-72 w-full object-cover md:h-96"
          />
          <div className="absolute inset-0 flex items-end bg-gradient-to-t from-[#062012]/85 via-[#062012]/20 to-transparent p-8 md:p-12">
            <p className="max-w-2xl font-heading text-2xl font-bold leading-snug text-white md:text-3xl">
              "Hope is restored one meal, one key, one mile at a time."
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
